import { DEFAULT_CURRENCY } from '@constants/global';

const numberFormatCache = new Map<string, Intl.NumberFormat>();

export interface FormatOptions {
  number: number;
  locale: string;
  currency?: string;
  shortened?: boolean;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
}
const createCacheKey = ({ locale, currency, shortened, maximumFractionDigits }: FormatOptions) => {
  return `${locale}-${currency}-${maximumFractionDigits}-${shortened}`;
};

export const formatNumber = (options: FormatOptions): string => {
  const cacheKey = createCacheKey(options);

  if (!numberFormatCache.has(cacheKey)) {
    const formatter = new Intl.NumberFormat(options.locale, {
      style: 'currency',
      currency: options.currency ?? DEFAULT_CURRENCY,
      minimumFractionDigits: options.minimumFractionDigits === 0 ? 0 : options.minimumFractionDigits ?? 2,
      maximumFractionDigits: options.maximumFractionDigits ?? 2,
      notation: options.shortened ? 'compact' : 'standard',
    });
    numberFormatCache.set(cacheKey, formatter);
  }
  const formatter = numberFormatCache.get(cacheKey);
  if (!formatter) {
    throw new Error(`Formatter not found for cache key: ${cacheKey}`);
  }
  return formatter.format(options.number);
};

export const formatValue = (options: FormatOptions) => {
  const cacheKey = createCacheKey(options);

  if (!numberFormatCache.has(cacheKey)) {
    const formatter = new Intl.NumberFormat(options.locale, {
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: options.maximumFractionDigits ?? 2,
      notation: options.shortened ? 'compact' : 'standard',
    });
    numberFormatCache.set(cacheKey, formatter);
  }
  const formatter = numberFormatCache.get(cacheKey);
  if (!formatter) {
    throw new Error(`Formatter not found for cache key: ${cacheKey}`);
  }
  return formatter.format(options.number);
};
